import type { Denomination, YieldProfile } from '../vaults/classification'

/**
 * Tolerant accessors for the two origins' loosely-typed rows.
 *
 * Both `/pools/latest` and `/vaults/latest` are external services that add
 * fields without a schema bump, and both mix numbers with numeric strings in
 * the same field across providers. Normalizing at the read site — rather than
 * trusting a declared type — is what keeps one provider's stray `"4.12"` from
 * becoming `NaN` three layers down.
 */

/**
 * Coerce to a finite number. Accepts numeric strings.
 *
 * Returns `undefined` for null/undefined/empty/non-finite — never `NaN`, and
 * never `0`. The distinction matters: `0 %` is a real rate (a collateral-only
 * leg), so a missing value must not masquerade as one.
 */
export function num(v: unknown): number | undefined {
  if (v == null || v === '') return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** Coerce to a non-empty string, else `undefined`. */
export function str(v: unknown): string | undefined {
  if (typeof v === 'string') return v.length > 0 ? v : undefined
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return undefined
}

/** Lowercased non-empty string — for addresses, which key everything. */
export function addr(v: unknown): string | undefined {
  return str(v)?.toLowerCase()
}

/** Narrow to a known `YieldProfile`, dropping anything unrecognised. */
export function yieldProfile(v: unknown): YieldProfile | undefined {
  return v === 'yield-bearing' || v === 'volatile' ? v : undefined
}

/** Narrow to a known `Denomination`. */
export function denomination(v: unknown): Denomination | undefined {
  return v === 'stable' || v === 'volatile' ? v : undefined
}

/**
 * Multiply a formatted (human-unit) amount by a USD price.
 *
 * Returns `undefined` when either input is missing, rather than `0` — a vault
 * we could not price must not sort as worthless next to one that genuinely is.
 */
export function usdValue(
  formatted: number | undefined,
  priceUsd: number | undefined,
): number | undefined {
  if (formatted === undefined || priceUsd === undefined) return undefined
  const v = formatted * priceUsd
  return Number.isFinite(v) ? v : undefined
}

/**
 * Format a raw base-unit integer string to human units.
 *
 * Uses BigInt for the integer part so large balances keep full precision, then
 * appends the fraction — `Number(raw) / 10 ** decimals` silently loses digits
 * above 2^53, which is well inside normal 18-decimal TVLs.
 */
export function formatRaw(
  raw: string | undefined,
  decimals: number,
): number | undefined {
  if (raw == null || raw === '') return undefined
  let value: bigint
  try {
    value = BigInt(raw)
  } catch {
    return undefined
  }
  if (decimals <= 0) return Number(value)
  const base = 10n ** BigInt(decimals)
  const whole = value / base
  const frac = value % base
  const n = Number(whole) + Number(frac) / Number(base)
  return Number.isFinite(n) ? n : undefined
}
